/**
 * Wizard state for Spark. Persisted to localStorage so a refresh mid-
 * flow keeps the user's work.
 *
 * `wasm_bytes` is held in memory only (never persisted — too big, and
 * re-hashing on refresh would be a UX surprise). If a refresh happens
 * after a WASM upload, the hash is retained but the bytes are gone;
 * the user has to re-drop the file before the final Sign step. The
 * Logic step detects this and nudges.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import type { DomainType, Hash256, ParameterDef, PortDefinition } from '@/api/types';
import type {
  ParameterValues,
  PortValues,
  Step1Values,
  Step2Values,
  Step3Values,
  Step4Values,
} from './schemas';

/** Number of steps in the wizard — 7 per the product brief. */
export const TOTAL_STEPS = 7;

export type WizardStep = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export interface WizardDraftPersisted {
  /** Step 1 */
  name: string;
  description: string;
  version: string;
  domain: DomainType;
  author_name: string;
  /** Step 2 / 3 — ports with editable schema text. */
  input_ports: PortValues[];
  output_ports: PortValues[];
  /** Step 4 — parameters (editable raw form). */
  parameters: ParameterValues[];
  /** Step 5 — hash only; bytes are in-memory. */
  wasm_hash: Hash256 | null;
  wasm_filename: string | null;
  wasm_size_bytes: number | null;
}

interface WizardState extends WizardDraftPersisted {
  currentStep: WizardStep;
  /** In-memory, not persisted. */
  wasm_bytes: Uint8Array | null;
  /** Set by the Coach handoff so Step 6 can render its banner. Not
   *  persisted — a refresh clears the claim so users who hand-fill
   *  a wizard don't see the banner. */
  coach_filled: boolean;
  setStep: (s: WizardStep) => void;
  applyStep1: (v: Step1Values) => void;
  applyStep2: (v: Step2Values) => void;
  applyStep3: (v: Step3Values) => void;
  applyStep4: (v: Step4Values) => void;
  applyWasm: (hash: Hash256 | null, bytes: Uint8Array | null, name: string | null, size: number | null) => void;
  markCoachFilled: () => void;
  clearCoachFilled: () => void;
  reset: () => void;
}

function emptyDraft(): WizardDraftPersisted {
  return {
    name: '',
    description: '',
    version: '1.0.0',
    domain: 'Generic',
    author_name: 'you',
    input_ports: [
      { name: 'in', data_type: 'Document', required: true, schema_text: '' },
    ],
    output_ports: [
      { name: 'out', data_type: 'Document', required: true, schema_text: '' },
    ],
    parameters: [],
    wasm_hash: null,
    wasm_filename: null,
    wasm_size_bytes: null,
  };
}

export const useSparkStore = create<WizardState>()(
  persist(
    (set) => ({
      currentStep: 1,
      ...emptyDraft(),
      wasm_bytes: null,
      coach_filled: false,
      setStep: (s) => set({ currentStep: s }),
      markCoachFilled: () => set({ coach_filled: true }),
      clearCoachFilled: () => set({ coach_filled: false }),
      applyStep1: (v) =>
        set({
          name: v.name,
          description: v.description,
          version: v.version,
          domain: v.domain,
          author_name: v.author_name,
        }),
      applyStep2: (v) => set({ input_ports: v.inputs }),
      applyStep3: (v) => set({ output_ports: v.outputs }),
      applyStep4: (v) => set({ parameters: v.parameters }),
      applyWasm: (wasm_hash, wasm_bytes, wasm_filename, wasm_size_bytes) =>
        set({ wasm_hash, wasm_bytes, wasm_filename, wasm_size_bytes }),
      reset: () =>
        set({
          currentStep: 1,
          wasm_bytes: null,
          coach_filled: false,
          ...emptyDraft(),
        }),
    }),
    {
      name: 'gi-engine.spark-draft',
      storage: createJSONStorage(() => localStorage),
      // Skip `wasm_bytes` (too big) and `currentStep` (UX reset on refresh
      // is desirable for a clean re-entry point).
      partialize: (s) => ({
        name: s.name,
        description: s.description,
        version: s.version,
        domain: s.domain,
        author_name: s.author_name,
        input_ports: s.input_ports,
        output_ports: s.output_ports,
        parameters: s.parameters,
        wasm_hash: s.wasm_hash,
        wasm_filename: s.wasm_filename,
        wasm_size_bytes: s.wasm_size_bytes,
      }),
    },
  ),
);

// ─── Derivations ────────────────────────────────────────────────────

/** Convert the wizard's raw form state into the typed port list the
 *  API expects. Empty `schema_text` yields `schema: undefined` (which
 *  is how the Rust wire form represents "no schema"). */
export function portsFromDraft(ports: PortValues[]): PortDefinition[] {
  return ports.map((p) => {
    const port: PortDefinition = {
      name: p.name,
      data_type: p.data_type,
      required: p.required,
    };
    if (p.schema_text) {
      port.schema = JSON.parse(p.schema_text) as unknown;
    }
    return port;
  });
}

/** Convert the wizard's raw parameter list into the typed shape. */
export function parametersFromDraft(params: ParameterValues[]): ParameterDef[] {
  return params.map((p) => {
    const def_value = coerceParamValue(p);
    const range = coerceParamRange(p);
    const out: ParameterDef = {
      name: p.name,
      description: p.description,
      default: def_value,
    };
    if (range !== undefined) out.range = range;
    return out;
  });
}

function coerceParamValue(p: ParameterValues): ParameterDef['default'] {
  switch (p.kind) {
    case 'Integer':
      return { integer: Number(p.default_text) };
    case 'Float':
      return { float: Number(p.default_text) };
    case 'Bool':
      return { bool: p.default_text === 'true' };
    case 'String':
      return { string: p.default_text };
    case 'Enum':
      return { enum: p.default_text };
  }
}

function coerceParamRange(p: ParameterValues): ParameterDef['range'] | undefined {
  if (p.kind === 'Integer' && p.range_min && p.range_max) {
    return { Integer: { min: Number(p.range_min), max: Number(p.range_max) } };
  }
  if (p.kind === 'Float' && p.range_min && p.range_max) {
    return { Float: { min: Number(p.range_min), max: Number(p.range_max) } };
  }
  if (p.kind === 'Enum') {
    const variants = p.enum_variants
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
    return { Enum: { variants } };
  }
  return undefined;
}
